import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Clock,
  Download,
  LayoutList,
  ScrollText,
  Search,
  ShieldCheck,
} from "lucide-react";
import type { AuditLogEntry, AuditLogFilterOptions } from "@crewledger/shared/schemas";
import { EmptyState, InfoBanner, ListTableSkeleton, PageHeader, useToast } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn } from "@/lib/utils";
import { MICROCOPY } from "@/lib/microcopy";
import { downloadAuditExportCsv, fetchAuditLogOptions, fetchAuditLogs } from "./api";
import { AuditDiffViewer } from "./components/audit-diff-viewer";
import { useAuditPermissions } from "./use-audit-permissions";

type ViewMode = "timeline" | "table";

function actionColor(action: string): string {
  if (action.includes("archived") || action.includes("rejected") || action.includes("failed")) {
    return "border-red-200 bg-red-50 text-red-700";
  }
  if (action.includes("approved") || action.includes("locked") || action.includes("published")) {
    return "border-green-200 bg-green-50 text-green-700";
  }
  if (action.includes("created") || action.includes("submit")) {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  return "border-slate-200 bg-slate-50 text-slate-700";
}

export function AuditLogsPage() {
  const { canRead, isReadOnly } = useAuditPermissions();
  const { toast } = useToast();
  const [view, setView] = useState<ViewMode>("timeline");
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [options, setOptions] = useState<AuditLogFilterOptions | null>(null);
  const [selected, setSelected] = useState<AuditLogEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [actorId, setActorId] = useState("");
  const [action, setAction] = useState("");
  const [siteId, setSiteId] = useState("");
  const [payPeriodId, setPayPeriodId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const debouncedQ = useDebouncedValue(q, 300);

  useEffect(() => {
    if (!canRead) return;
    fetchAuditLogOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() => { setOptions({ actors: [], actions: [], sites: [], payPeriods: [] }); });
  }, [canRead]);

  useEffect(() => {
    if (!canRead) return;
    setLoading(true);
    fetchAuditLogs({
      pageSize: 100,
      actorId: actorId || undefined,
      action: action || undefined,
      siteId: siteId || undefined,
      payPeriodId: payPeriodId || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      q: debouncedQ || undefined,
    })
      .then((res) => {
        setLogs(res.data);
        setTotal(res.meta.total ?? res.data.length);
        setSelected((prev) => {
          if (prev && res.data.some((e) => e.id === prev.id)) return prev;
          return res.data[0] ?? null;
        });
      })
      .catch(() => { setLogs([]); })
      .finally(() => { setLoading(false); });
  }, [canRead, actorId, action, siteId, payPeriodId, dateFrom, dateTo, debouncedQ]);

  if (!canRead) {
    return <Navigate to="/dashboard" replace />;
  }

  const exportFilters = {
    actorId: actorId || undefined,
    action: action || undefined,
    siteId: siteId || undefined,
    payPeriodId: payPeriodId || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    q: q || undefined,
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Audit Trail"
        description="Immutable record of changes, approvals, and exports — the Werkio Audit Trail."
        badge={
          isReadOnly ? (
            <span className="rounded-full bg-navy-deep/5 px-2.5 py-0.5 text-xs font-semibold text-navy-blueprint">
              Read-only
            </span>
          ) : undefined
        }
        actions={
          <div className="flex flex-wrap gap-2">
            <div className="flex rounded-lg border bg-bg-light p-0.5">
              <button
                type="button"
                onClick={() => { setView("timeline"); }}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium",
                  view === "timeline" ? "bg-card-white shadow-sm" : "text-muted-foreground",
                )}
              >
                <Clock className="h-3.5 w-3.5" />
                Timeline
              </button>
              <button
                type="button"
                onClick={() => { setView("table"); }}
                className={cn(
                  "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium",
                  view === "table" ? "bg-card-white shadow-sm" : "text-muted-foreground",
                )}
              >
                <LayoutList className="h-3.5 w-3.5" />
                Table
              </button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void downloadAuditExportCsv(exportFilters).catch((err: unknown) => {
                  toast({
                    variant: "error",
                    title: "Export failed",
                    description: err instanceof Error ? err.message : undefined,
                  });
                });
              }}
            >
              <Download className="mr-1.5 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        }
      />

      <InfoBanner icon={ShieldCheck} variant="info">
        {MICROCOPY.auditLogSaved} {MICROCOPY.lockedPayPeriod}
      </InfoBanner>

      {/* Filters */}
      <div className="grid gap-3 rounded-xl border bg-card-white p-4 lg:grid-cols-4 xl:grid-cols-7">
        <div className="relative lg:col-span-2 xl:col-span-2">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search action or reason…"
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
            className="pl-9"
          />
        </div>
        <select
          value={actorId}
          onChange={(e) => { setActorId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All actors</option>
          {options?.actors.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name ?? a.email ?? a.id.slice(0, 8)}
            </option>
          ))}
        </select>
        <select
          value={action}
          onChange={(e) => { setAction(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All actions</option>
          {options?.actions.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
        <select
          value={siteId}
          onChange={(e) => { setSiteId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All sites</option>
          {options?.sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.code}
            </option>
          ))}
        </select>
        <select
          value={payPeriodId}
          onChange={(e) => { setPayPeriodId(e.target.value); }}
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
        >
          <option value="">All pay periods</option>
          {options?.payPeriods.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); }} />
        <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); }} />
      </div>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        {/* Main list */}
        <div className="min-w-0">
          {loading ? (
            <ListTableSkeleton rows={8} cols={4} />
          ) : logs.length === 0 ? (
            <EmptyState
              icon={ScrollText}
              title="No audit entries"
              description="Try adjusting filters, or save a daily entry, approve a pay period, or queue an export."
            />
          ) : view === "timeline" ? (
            <ol className="relative space-y-0 border-l-2 border-navy-deep/10 pl-6">
              {logs.map((entry, index) => (
                <li key={entry.id} className="relative pb-6">
                  <span
                    className={cn(
                      "absolute -left-[1.65rem] flex h-5 w-5 items-center justify-center rounded-full border-2 border-card-white",
                      selected?.id === entry.id ? "bg-orange-construction" : "bg-navy-blueprint",
                    )}
                  />
                  <button
                    type="button"
                    onClick={() => { setSelected(entry); }}
                    className={cn(
                      "w-full rounded-xl border p-4 text-left transition-colors",
                      selected?.id === entry.id
                        ? "border-orange-construction/40 bg-orange-50/30 shadow-sm"
                        : "border-border bg-card-white hover:bg-bg-light/60",
                    )}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <span
                          className={cn(
                            "inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                            actionColor(entry.action),
                          )}
                        >
                          {entry.actionLabel}
                        </span>
                        <p className="mt-2 font-medium text-navy-deep">
                          {entry.targetLabel ?? entry.targetType}
                          {entry.siteCode ? (
                            <span className="ml-2 font-mono text-xs text-muted-foreground">
                              {entry.siteCode}
                            </span>
                          ) : null}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {entry.actorName ?? entry.actorEmail ?? "System"}
                          {entry.reason ? ` · ${entry.reason}` : ""}
                        </p>
                      </div>
                      <time className="shrink-0 text-xs text-muted-foreground">
                        {new Date(entry.createdAt).toLocaleString()}
                      </time>
                    </div>
                  </button>
                  {index < logs.length - 1 ? null : (
                    <p className="mt-2 text-xs text-muted-foreground">{total} entries total</p>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <div className="overflow-hidden rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
              <table className="w-full text-sm">
                <thead className="border-b bg-navy-deep/3 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Time</th>
                    <th className="px-3 py-3 font-semibold">Actor</th>
                    <th className="px-3 py-3 font-semibold">Action</th>
                    <th className="px-3 py-3 font-semibold">Target</th>
                    <th className="px-3 py-3 font-semibold">Site</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((entry) => (
                    <tr
                      key={entry.id}
                      onClick={() => { setSelected(entry); }}
                      className={cn(
                        "cursor-pointer border-b border-border/40 transition-colors hover:bg-bg-light/60",
                        selected?.id === entry.id && "bg-orange-50/40",
                      )}
                    >
                      <td className="px-4 py-3 font-mono text-xs whitespace-nowrap">
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-3">{entry.actorName ?? "System"}</td>
                      <td className="px-3 py-3">
                        <span className="text-xs font-medium">{entry.actionLabel}</span>
                      </td>
                      <td className="px-3 py-3 text-xs">
                        {entry.targetLabel ?? entry.targetType}
                      </td>
                      <td className="px-3 py-3 font-mono text-xs">{entry.siteCode ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Diff panel */}
        <aside className="lg:sticky lg:top-4 lg:self-start">
          <div className="overflow-hidden rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
            <div className="flex items-center gap-2 border-b bg-navy-deep/3 px-4 py-3">
              <ShieldCheck className="h-4 w-4 text-navy-blueprint" />
              <h3 className="font-semibold text-navy-deep">Event detail</h3>
            </div>
            {selected ? (
              <div className="space-y-4 p-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-muted-foreground">Action</p>
                  <p className="font-medium text-navy-deep">{selected.actionLabel}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Actor</p>
                    <p>{selected.actorName ?? selected.actorEmail ?? "System"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Time</p>
                    <p className="font-mono text-xs">
                      {new Date(selected.createdAt).toLocaleString()}
                    </p>
                  </div>
                  {selected.siteCode ? (
                    <div>
                      <p className="text-xs text-muted-foreground">Site</p>
                      <p>{selected.siteCode}</p>
                    </div>
                  ) : null}
                  {selected.payPeriodLabel ? (
                    <div>
                      <p className="text-xs text-muted-foreground">Pay period</p>
                      <p>{selected.payPeriodLabel}</p>
                    </div>
                  ) : null}
                </div>
                {selected.reason ? (
                  <div className="rounded-md bg-bg-light px-3 py-2 text-sm">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">Reason</p>
                    <p className="mt-1">{selected.reason}</p>
                  </div>
                ) : null}
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Changes
                  </p>
                  <AuditDiffViewer entry={selected} />
                </div>
              </div>
            ) : (
              <p className="p-6 text-sm text-muted-foreground">Select an entry to view details.</p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
